import keys from 'lodash/keys';
import { defineStore } from 'pinia';
import { Color } from 'tvision-color';
import { computed, ref } from 'vue';

import { DARK_CHART_COLORS, LIGHT_CHART_COLORS, TColorSeries } from '@/config/color';
import STYLE_CONFIG from '@/config/style';
import { store } from '@/store';
import type { ThemeMode } from '@/types/common/ui';
import { generateColorMap, insertThemeStylesheet } from '@/utils/color';

const state: Record<string, any> = {
  ...STYLE_CONFIG,
  showSettingPanel: false,
  colorList: {} as TColorSeries,
  chartColors: LIGHT_CHART_COLORS,
};

export type TState = typeof state;
export type TStateKey = keyof typeof state;

export const useSettingStore = defineStore(
  'setting',
  () => {
    /**
     * 是否显示页脚
     */
    const showFooter = ref(false);
    /**
     * 是否使用紧凑侧边栏
     */
    const isSidebarCompact = ref(false);
    /**
     * 是否显示面包屑
     */
    const showBreadcrumb = ref(false);
    /**
     * 是否自动折叠菜单
     */
    const menuAutoCollapsed = ref(false);
    /**
     * 模式
     */
    const mode = ref('light');
    /**
     * 布局
     */
    const layout = ref('side');
    /**
     * 拆分菜单
     */
    const splitMenu = ref(false);
    /**
     * 侧边栏模式
     */
    const sideMode = ref('light');
    /**
     * 页脚
     */
    const isFooterAside = ref(false);
    /**
     * 是否固定侧边栏
     */
    const isSidebarFixed = ref(true);
    /**
     * 是否固定顶部
     */
    const isHeaderFixed = ref(true);
    /**
     * 是否使用标签页路由
     */
    const isUseTabsRouter = ref(false);
    /**
     * 是否显示顶部
     */
    const showHeader = ref(true);
    /**
     * 主题色
     */
    const brandTheme = ref('#0052D9');
    /**
     * 是否显示设置面板
     */
    const showSettingPanel = ref(false);
    /**
     * 颜色列表
     */
    const colorList = ref({} as TColorSeries);
    /**
     * 图表颜色
     */
    const chartColors = ref(LIGHT_CHART_COLORS);

    /**
     * 是否显示侧边栏
     */
    const showSidebar = computed(() => state.layout !== 'top');
    /**
     * 是否显示侧边栏 Logo
     */
    const showSidebarLogo = computed(() => state.layout === 'side');
    /**
     * 是否显示顶部 Logo
     */
    const showHeaderLogo = computed(() => state.layout !== 'side');
    /**
     * 是否显示顶部标题
     */
    const displayMode = computed((): ThemeMode => {
      if (state.mode === 'auto') {
        const media = window.matchMedia('(prefers-color-scheme:dark)');
        if (media.matches) {
          return 'dark';
        }
        return 'light';
      }
      return state.mode as ThemeMode;
    });
    /**
     * 是否显示顶部标题
     */
    const displaySideMode = (): ThemeMode => {
      return state.sideMode as ThemeMode;
    };

    /**
     * 获取当前主题色
     *
     * @returns 当前主题色
     */
    function getMediaColor() {
      const media = window.matchMedia('(prefers-color-scheme:dark)');
      if (media.matches) {
        return 'dark';
      }
      return 'light';
    }

    async function changeMode(_mode: ThemeMode | 'auto') {
      let theme = _mode;

      if (_mode === 'auto') {
        theme = getMediaColor();
      }
      const isDarkMode = theme === 'dark';

      document.documentElement.setAttribute('theme-mode', isDarkMode ? 'dark' : '');

      chartColors.value = isDarkMode ? DARK_CHART_COLORS : LIGHT_CHART_COLORS;
    }

    async function changeSideMode(_mode: ThemeMode) {
      const isDarkMode = _mode === 'dark';

      document.documentElement.setAttribute('side-mode', isDarkMode ? 'dark' : '');
    }

    function changeBrandTheme(_brandTheme: string) {
      // 以主题色加显示模式作为键
      const colorKey = `${_brandTheme}[${displayMode.value}]`;
      let colorMap = colorList.value[colorKey];
      // 如果不存在色阶，就需要计算
      if (colorMap === undefined) {
        const [{ colors: newPalette, primary: brandColorIndex }] = Color.getColorGradations({
          colors: [_brandTheme],
          step: 10,
          remainInput: false, // 是否保留输入 不保留会矫正不合适的主题色
        });
        colorMap = generateColorMap(_brandTheme, newPalette, displayMode.value, brandColorIndex);
        colorList.value[colorKey] = colorMap;
      }
      // TODO 需要解决不停切换时有反复插入 style 的问题
      insertThemeStylesheet(_brandTheme, colorMap, displayMode.value);
      document.documentElement.setAttribute('theme-color', _brandTheme);
    }
    /**
     * 更新配置
     *
     * @param payload 配置
     */
    function updateConfig(payload: Partial<TState>) {
      for (const key in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
          if (key === 'showFooter') showFooter.value = payload[key];
          if (key === 'isSidebarCompact') isSidebarCompact.value = payload[key];
          if (key === 'showBreadcrumb') showBreadcrumb.value = payload[key];
          if (key === 'menuAutoCollapsed') menuAutoCollapsed.value = payload[key];
          if (key === 'layout') layout.value = payload[key];
          if (key === 'splitMenu') splitMenu.value = payload[key];
          if (key === 'isFooterAside') isFooterAside.value = payload[key];
          if (key === 'isSidebarFixed') isSidebarFixed.value = payload[key];
          if (key === 'isHeaderFixed') isHeaderFixed.value = payload[key];
          if (key === 'isUseTabsRouter') isUseTabsRouter.value = payload[key];
          if (key === 'showHeader') showHeader.value = payload[key];
          if (key === 'showSettingPanel') showSettingPanel.value = payload[key];
          if (key === 'colorList') colorList.value = payload[key];
          if (key === 'chartColors') chartColors.value = payload[key];
          if (key === 'mode') changeMode(payload[key] as ThemeMode);
          if (key === 'sideMode') changeSideMode(payload[key] as ThemeMode);
          if (key === 'brandTheme') changeBrandTheme(payload[key]);
        }
      }
    }

    return {
      showFooter,
      isSidebarCompact,
      showBreadcrumb,
      menuAutoCollapsed,
      mode,
      layout,
      splitMenu,
      sideMode,
      isFooterAside,
      isSidebarFixed,
      isHeaderFixed,
      isUseTabsRouter,
      showHeader,
      brandTheme,
      showSettingPanel,
      colorList,
      chartColors,
      showSidebar,
      showSidebarLogo,
      showHeaderLogo,
      displayMode,
      displaySideMode,
      updateConfig,
      changeMode,
      changeSideMode,
      changeBrandTheme,
    };
  },
  {
    persist: {
      pick: [...keys(STYLE_CONFIG), 'colorList', 'chartColors'],
    },
  },
);

export function getSettingStore() {
  return useSettingStore(store);
}
